/**
 * CommandSpecial — pure direction+SPECIAL command parser.
 *
 * No Phaser import — fully unit-testable in Node. The Player adapter calls
 * resolveCommandSpecial() when SPECIAL is pressed (justDown) after the super
 * and motion-input branches, passing current held directions and the existing
 * DashState.active flag (never duplicating dash detection).
 *
 * Priority order (most specific first):
 *   1. dash   → dash + SPECIAL (e.g. Spinning Piledriver)
 *   2. air    → airborne + SPECIAL
 *   3. down   → DOWN held + SPECIAL
 *   4. back   → direction opposite to facing held + SPECIAL
 *   5. toward → direction matching facing held + SPECIAL
 *   6. null   → no command matched (neutral SPECIAL → fallthrough or ignore)
 *
 * Direction semantics:
 *   toward = held direction matching facing
 *   back   = held direction opposite to facing
 */

/** Input snapshot passed by Player each frame when SPECIAL is justDown. */
export interface CommandSpecialInput {
  /** Current facing direction (1 = right, -1 = left). */
  facing: 1 | -1;
  /** Which directional inputs are currently held. */
  held: {
    up: boolean;
    down: boolean;
    left: boolean;
    right: boolean;
  };
  /** True when the player is in a double-tap sprint (from DashState.active). */
  dashActive: boolean;
  /** True when the player is off the ground plane. */
  airborne: boolean;
}

/** A map from command string to move id. */
export type CommandTable = Record<string, string>;

/**
 * Resolve which command-special id fires given current input, or null if none
 * in the table matches.
 *
 * @param input   Snapshot of current player state.
 * @param table   Hero-specific command table (e.g. RAY_COMMAND_TABLE).
 * @returns       Move id string, or null if no entry matches.
 */
export function resolveCommandSpecial(
  input: CommandSpecialInput,
  table: CommandTable,
): string | null {
  const { facing, held, dashActive, airborne } = input;

  // Determine held horizontal direction.
  const heldRight = held.right && !held.left;
  const heldLeft = held.left && !held.right;

  const towardHeld = facing === 1 ? heldRight : heldLeft;
  const backHeld = facing === 1 ? heldLeft : heldRight;

  // Priority 1: dash
  if (dashActive && table['dash'] !== undefined) return table['dash']!;

  // Priority 2: air
  if (airborne && table['air'] !== undefined) return table['air']!;

  // Priority 3: down
  if (held.down && table['down'] !== undefined) return table['down']!;

  // Priority 4: back
  if (backHeld && table['back'] !== undefined) return table['back']!;

  // Priority 5: toward
  if (towardHeld && table['toward'] !== undefined) return table['toward']!;

  // No match.
  return null;
}
